/**
 * OverridePolicy.tsx — /settings/override-policy
 *
 * Manager configuration for the Override System.
 * Mi6 palette: Navy #0B0B0F · Red #DC2626 · background #FAFAF7
 */

import { useState, useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  loadOverridePolicy,
  saveOverridePolicy,
  type OverridePolicy,
  type OverrideMode,
  type OverrideReason,
  OVERRIDE_REASON_LABELS,
  DEFAULT_OVERRIDE_POLICY,
} from "@/lib/override-store";

const MI6_BLACK  = "#0B0B0F";
const MI6_RED   = "#DC2626";
const MI6_BG    = "#FAFAF7";

const ALL_REASONS: OverrideReason[] = [
  "verbal_commit",
  "existing_expansion",
  "internal_processes",
  "exec_sponsor",
  "legal_review",
  "champion_guarantee",
];

const MODE_OPTIONS: { value: OverrideMode; label: string; description: string }[] = [
  {
    value: "off",
    label: "Off — system score only",
    description:
      "No overrides allowed. Forecast reflects Helix scores only. Use only when a team needs to build scoring discipline first.",
  },
  {
    value: "rep_allowed",
    label: "Rep-allowed — reps file and self-approve",
    description:
      "Reps can file overrides without manager approval. Each override requires a reason, close-date commitment, and justification note. Default — reps decide, the platform captures.",
  },
  {
    value: "manager_only",
    label: "Manager-only — reps request, manager approves",
    description:
      "Reps submit override requests. Manager reviews and either approves or denies before they appear as Commit on executive dashboards.",
  },
];

function ModeModeBanner({ policy }: { policy: OverridePolicy }) {
  const opt = MODE_OPTIONS.find((o) => o.value === policy.mode);
  const color =
    policy.mode === "off"
      ? "#6B6B70"
      : policy.mode === "rep_allowed"
      ? "#CA8A04"
      : MI6_BLACK;
  return (
    <div
      style={{
        background: `${color}12`,
        border: `1px solid ${color}30`,
        borderRadius: "10px",
        padding: "14px 20px",
        marginBottom: "28px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <div
        style={{
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <div>
        <div style={{ fontSize: "13px", fontWeight: 700, color }}>
          Current mode: {opt?.label ?? policy.mode}
        </div>
        <div style={{ fontSize: "12px", color: "#6B6B70", marginTop: "2px" }}>
          {opt?.description}
        </div>
      </div>
    </div>
  );
}

export default function OverridePolicy() {
  const [policy, setPolicy] = useState<OverridePolicy>(DEFAULT_OVERRIDE_POLICY);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setPolicy(loadOverridePolicy());
  }, []);

  function setMode(mode: OverrideMode) {
    setPolicy((p) => ({ ...p, mode }));
    setSaved(false);
  }

  function toggleReason(reason: OverrideReason) {
    setPolicy((p) => {
      const included = p.allowed_reasons.includes(reason);
      const next = included
        ? p.allowed_reasons.filter((r) => r !== reason)
        : [...p.allowed_reasons, reason];
      return { ...p, allowed_reasons: next };
    });
    setSaved(false);
  }

  function handleSave() {
    saveOverridePolicy(policy);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <OperatorShell breadcrumbs={[{ label: "Override Policy" }]} pageTitle="Override Policy" pageMeta="Manager configuration">

      <div style={{ maxWidth: "760px", margin: "0 auto", padding: "40px 24px" }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: "20px" }}>
          <a
            href="/settings/email"
            style={{ fontSize: "12px", color: "#6B6B70", textDecoration: "none", fontWeight: 500 }}
          >
            ← Settings
          </a>
        </div>

        {/* Title */}
        <h1 style={{ fontSize: "26px", fontWeight: 900, color: MI6_BLACK, margin: "0 0 6px", letterSpacing: "-0.01em" }}>
          Override Policy
        </h1>
        <p style={{ fontSize: "14px", color: "#6B6B70", margin: "0 0 32px", lineHeight: "1.5", maxWidth: "600px" }}>
          Control how your team can override system scores in the forecast. Overrides
          require a documented reason, close-date commitment, and justification. All
          overrides are tracked and resolved — accuracy is reported per ae.
        </p>

        {/* Current mode banner */}
        <ModeModeBanner policy={policy} />

        {/* Mode selection */}
        <section style={{ marginBottom: "32px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#525258", marginBottom: "12px" }}>
            Override Mode
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {MODE_OPTIONS.map((opt) => {
              const selected = policy.mode === opt.value;
              return (
                <label
                  key={opt.value}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    padding: "16px 18px",
                    background: selected ? `${MI6_BLACK}08` : "#FFFFFF",
                    border: `1.5px solid ${selected ? MI6_BLACK : "#E5E5E7"}`,
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                  }}
                >
                  <input
                    type="radio"
                    name="override_mode"
                    value={opt.value}
                    checked={selected}
                    onChange={() => setMode(opt.value)}
                    style={{ marginTop: "2px", accentColor: MI6_BLACK, cursor: "pointer" }}
                  />
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: MI6_BLACK }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6B6B70", marginTop: "3px", lineHeight: "1.5" }}>
                      {opt.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </section>

        {/* Allowed reasons */}
        <section style={{ marginBottom: "32px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#525258", marginBottom: "4px" }}>
            Allowed Override Reasons
          </div>
          <div style={{ fontSize: "12px", color: "#6B6B70", marginBottom: "12px" }}>
            Uncheck any reason to prevent reps from selecting it when filing an override.
          </div>
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            {ALL_REASONS.map((reason, i) => {
              const checked = policy.allowed_reasons.includes(reason);
              return (
                <label
                  key={reason}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "13px 18px",
                    borderTop: i > 0 ? "1px solid #EFEFEE" : "none",
                    cursor: "pointer",
                    background: checked ? "transparent" : "#FEF2F2",
                    transition: "background 0.1s",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleReason(reason)}
                    style={{ accentColor: MI6_BLACK, cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "13px", fontWeight: 600, color: checked ? MI6_BLACK : "#8E8E94" }}>
                    {OVERRIDE_REASON_LABELS[reason]}
                  </span>
                </label>
              );
            })}
          </div>
        </section>

        {/* Co-sign threshold */}
        <section style={{ marginBottom: "32px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#525258", marginBottom: "4px" }}>
            Manager Co-sign Threshold
          </div>
          <div style={{ fontSize: "12px", color: "#6B6B70", marginBottom: "10px" }}>
            Overrides on deals larger than this value require manager co-sign before appearing as
            Commit on the executive dashboard. Set to 0 to require co-sign on all overrides.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", fontWeight: 700, color: MI6_BLACK }}>$</span>
            <input
              type="number"
              min={0}
              step={1000}
              value={policy.cosign_threshold_usd}
              onChange={(e) => {
                setPolicy((p) => ({ ...p, cosign_threshold_usd: Number(e.target.value) }));
                setSaved(false);
              }}
              style={{
                width: "160px",
                padding: "9px 12px",
                border: "1.5px solid #C9C9CC",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                color: MI6_BLACK,
                background: "#FFFFFF",
                outline: "none",
              }}
            />
            <span style={{ fontSize: "12px", color: "#8E8E94" }}>USD deal value</span>
          </div>
        </section>

        {/* Default max close days */}
        <section style={{ marginBottom: "40px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#525258", marginBottom: "4px" }}>
            Default Max Close-Date Commitment
          </div>
          <div style={{ fontSize: "12px", color: "#6B6B70", marginBottom: "10px" }}>
            Maximum number of days out a rep can commit to when filing an override close date.
            Overrides with commitments beyond this window will show a warning.
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="number"
              min={1}
              max={365}
              value={policy.default_max_close_days}
              onChange={(e) => {
                setPolicy((p) => ({ ...p, default_max_close_days: Number(e.target.value) }));
                setSaved(false);
              }}
              style={{
                width: "100px",
                padding: "9px 12px",
                border: "1.5px solid #C9C9CC",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                color: MI6_BLACK,
                background: "#FFFFFF",
                outline: "none",
              }}
            />
            <span style={{ fontSize: "12px", color: "#8E8E94" }}>days from today</span>
          </div>
        </section>

        {/* Save */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={handleSave}
            style={{
              padding: "11px 28px",
              background: MI6_BLACK,
              color: "#FFFFFF",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: "pointer",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Save Policy
          </button>
          {saved && (
            <span style={{ fontSize: "13px", color: "#16A34A", fontWeight: 600 }}>
              Policy saved.
            </span>
          )}
        </div>

        {/* Info note */}
        <div
          style={{
            marginTop: "40px",
            padding: "16px 20px",
            background: "#FFFFFF",
            border: "1px solid #E5E5E7",
            borderRadius: "10px",
          }}
        >
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#525258", marginBottom: "8px", letterSpacing: "0.05em", textTransform: "uppercase" }}>
            How Overrides Work
          </div>
          <ul style={{ margin: 0, padding: "0 0 0 16px", fontSize: "12px", color: "#6B6B70", lineHeight: "1.8" }}>
            <li>Every override requires: reason, close-date commitment, and justification.</li>
            <li>Active overrides appear as yellow badges on deal cards.</li>
            <li>The Override Watch tile on Manager and Executive dashboards shows all active commits.</li>
            <li>Every override is resolved as: closed on date / slipped / lost.</li>
            <li>Per-AE override accuracy is tracked and shown in Reporting.</li>
          </ul>
        </div>

      </div>
    </OperatorShell>
  );
}
